<template>
    <div v-if="isOpen" class="modal-overlay">
        <div class="form-modal">
            <!-- Show loading spinner or placeholder if form is loading -->
            <div v-if="loading" class="loading-placeholder">
                <p>{{ $translate('Loading form...') }}</p>
                <div class="loader"></div>
            </div>

            <!-- Show form content once loading is complete -->
            <div v-else>
                <h3 class="text-2xl font-bold mb-4 text-gray-900">{{ form.title }}</h3>
                <p class="mb-6 text-gray-600">{{ form.description }}</p>

                <form @submit.prevent="submitForm">
                    <!-- Render dynamic form fields -->
                    <div v-for="(field, index) in form.fields" :key="index" class="mb-6">
                        <!-- Handle different field types -->
                        <template v-if="field.type === 'line'">
                            <hr class="my-4 border-t border-gray-300" />
                        </template>

                        <template v-if="field.type === 'custom_text'">
                            <div class="bg-gray-50 p-4 rounded-lg shadow-sm">
                                <p v-html="field.content" class="text-gray-700"></p>
                            </div>
                        </template>

                        <template v-if="field.type === 'alert'">
                            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
                                <strong class="text-yellow-700">{{ field.title }}</strong>
                                <p v-html="field.content" class="text-yellow-600"></p>
                            </div>
                        </template>

                        <!-- Use dynamic component based on field type -->
                        <component :is="getFieldComponent(field)" :field="field" v-model="field.value" />

                        <!-- Display validation error if present -->
                        <p v-if="errors[field.id]" class="text-red-500 text-sm mt-2">
                            {{ errors[field.id] }}
                        </p>
                    </div>

                    <!-- Buttons -->
                    <div class="flex justify-end space-x-4">
                        <button @click="closeModal" type="button"
                            class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all shadow-sm">
                            {{ $translate('Close') }}
                        </button>
                        <button type="submit"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm">
                            {{ $translate('Submit Form') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>


<script>
import TextField from '@/components/modals/form/TextField.vue';
import TextareaField from '@/components/modals/form/TextareaField.vue';
import RadioField from '@/components/modals/form/RadioField.vue';
import SelectField from '@/components/modals/form/SelectField.vue';
import CheckboxField from '@/components/modals/form/CheckboxField.vue';
import CheckboxesField from '@/components/modals/form/CheckboxesField.vue';
import FileField from '@/components/modals/form/FileField.vue';
import { cookie } from '@/utils/cookie';
import { notify } from 'notiwind';
import { translate } from '@/utils/translate';

export default {
    name: 'FormModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        formId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            form: {
                title: '',
                description: '',
                fields: [],
            },
            sessionId: cookie(),
            brandId: window.vueAppConfig.info.id,
            errors: {}, // To store validation errors
            loading: true, // To track if form data is being loaded
        };
    },
    components: {
        TextField,
        TextareaField,
        RadioField,
        SelectField,
        CheckboxField,
        CheckboxesField,
        FileField,
    },
    mounted() {
        if (this.isOpen) {
            this.loadForm();
        }
    },
    methods: {
        closeModal() {
            this.$emit('close');
        },

        // Fetch form data from API
        loadForm() {
            this.loading = true; // Start loading
            const apiUrl = `${window.vueAppConfig.endpoint}/api/forms/v1/fetch`;

            const payload = {
                form_id: this.formId,
                session_id: this.sessionId,
                brand_id: this.brandId,
            };

            fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status) {
                        this.form = data.data; // Load form data into the component
                    } else {
                        notify({
                            title: translate("Error"),
                            text: data.message,
                            type: "error",
                            group: "error"
                        }, 10000);
                    }
                })
                .catch((error) => {
                    notify({
                        title: translate("Error"),
                        text: error,
                        type: "error",
                        group: "error"
                    }, 10000);
                })
                .finally(() => {
                    this.loading = false; // Stop loading once the form data is loaded
                });
        },

        // Handle form submission
        submitForm() {
            const formData = new FormData();
            this.errors = {}; // Clear previous errors

            this.form.fields.forEach((field) => {
                // Ensure field has a valid identifier (id or hash)
                const fieldIdentifier = field.hash || field.id;
                if (!fieldIdentifier) {
                    return;
                }

                // Handle file inputs
                if (field.type === 'file') {
                    const file = field.value; // Access the file directly from field.value
                    if (file) {
                        formData.append(fieldIdentifier, file);
                    }
                } else if (field.type === 'checkboxes') {
                    // Check if field.value is a JSON string, and parse it to an array if so
                    let values = field.value;
                    if (typeof field.value === 'string') {
                        try {
                            values = JSON.parse(field.value);
                        } catch (e) {
                            console.error('Failed to parse checkbox values:', e);
                        }
                    }

                    // Ensure values is an array and append each value individually
                    values = Array.isArray(values) ? values : [values];
                    values.forEach(value => {
                        formData.append(`${fieldIdentifier}[]`, value); // Append each value with the field name as an array
                    });
                } else {
                    // For all other fields (text, select, radio, etc.)
                    formData.append(fieldIdentifier, field.value || '');
                }
            });

            formData.append('form_id', this.formId);
            formData.append('session_id', this.sessionId);

            // Submit the form data
            fetch(`${window.vueAppConfig.endpoint}/api/forms/v1/submission`, {
                method: 'POST',
                body: formData,
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.status) {

                        notify({
                            title: translate("Success"),
                            text: data.message,
                            type: "success",
                            group: "info"
                        }, 10000);


                        this.closeModal();
                    } else {
                        this.handleErrors(data); // Handle server-side validation errors
                    }
                })
                .catch((error) => {
                    notify({ title: 'Error', text: error, type: 'error' });
                });
        },


        // Handle server-side validation errors
        handleErrors(responseData) {
            if (responseData.status === false && responseData.message && responseData.field) {
                const fieldHash = responseData.field;

                // Set error for the relevant field by hash (ID)
                this.errors[fieldHash] = responseData.message;

                notify({
                    title: translate("Error"),
                    text: responseData.message,
                    type: "error",
                    group: "error"
                }, 10000);



            }
        },

        // Get component for the field type
        getFieldComponent(field) {
            switch (field.type) {
                case 'text':
                    return 'TextField';
                case 'textarea':
                    return 'TextareaField';
                case 'radio':
                    return 'RadioField';
                case 'select':
                    return 'SelectField';
                case 'checkbox':
                    return 'CheckboxField';
                case 'checkboxes':
                    return 'CheckboxesField';
                case 'file':
                    return 'FileField';
                default:
                    return 'UnsupportedField';
            }
        },
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.loadForm(); // Reload the form if modal opens
            }
        },
    },
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 9999;
    overflow-y: auto;
    padding: 1rem;
}

.form-modal {
    background-color: #fff;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    max-width: 600px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.loading-placeholder {
    text-align: center;
    margin-top: 2rem;
}

.loader {
    border: 4px solid #f3f3f3;
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 36px;
    height: 36px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 640px) {
    .form-modal {
        padding: 1.5rem;
        max-width: 100%;
    }
}

.text-red-500 {
    color: #f56565;
}
</style>
